import React, { useState, useEffect } from 'react';
import {
    Card,
    Text,
    Button,
    ProgressBar,
    Badge,
    Avatar,
    Spinner,
    Tab,
    TabList,
} from '@fluentui/react-components';
import {
    Calendar24Regular,
    VideoClip24Regular,
    Document24Regular,
    Warning24Regular,
    Clock24Regular,
    Person24Regular,
    DeviceMeetingRoom24Regular,
    Alert24Regular,
    Call24Regular,
    Note24Regular,
    Money24Regular,
} from '@fluentui/react-icons';
import { apiService } from '../../../services/api/apiService';
import { socketService } from '../../../services/socket/socketService';
import { teamsService, TeamsUser } from '../../../services/teams/teamsService';
import { PatientVerification } from '../../Common/PatientVerification/PatientVerification';
import { ConsentCapture } from '../../Common/ConsentCapture/ConsentCapture';
import { NoteReviewInterface } from '../../Common/NoteGeneration/NoteReviewInterface';
import { RealTimeAlerts } from '../../Common/NoteGeneration/RealTimeAlerts';
import { BillingReviewInterface } from '../../Common/Billing/BillingReviewInterface';

interface DoctorMetrics {
    todaysConsultations: { completed: number; total: number };
    nextAppointment: {
        time: string;
        patientName: string;
        minutesUntil: number;
    } | null;
    emergencyCalls: number;
    pendingNotes: number;
}

interface Consultation {
    id: string;
    scheduledTime: string;
    patientName: string;
    patientId: string;
    mrn: string;
    consultationType: string;
    priority: 'urgent' | 'standard' | 'routine';
    status: 'scheduled' | 'in_progress' | 'completed' | 'emergency';
    deviceId: string;
    deviceLocation: string;
    teamsId: string;
    interpreterRequired: boolean;
    language: string;
    notes: string;
    minutesUntil: number;
    estimatedDuration: number;
}

interface TeamsNotification {
    id: string;
    consultationId: string;
    message: string;
    type: 'reminder' | 'assignment' | 'emergency';
    timestamp: string;
    read: boolean;
}

interface PatientInfo {
    id: string;
    name: string;
    mrn: string;
    dateOfBirth: string;
    photo?: string;
    age: number;
    gender: string;
    language: string;
    interpreterRequired: boolean;
    medicalConditions: string[];
    allergies: string[];
    riskLevel: 'low' | 'medium' | 'high';
    consultationType: string;
    priority: 'urgent' | 'standard' | 'routine';
}

interface ConsentOptions {
    audioRecording: boolean;
    videoRecording: boolean;
    transcription: boolean;
    translation: boolean;
    aiAnalysis: boolean;
    dataSharing: 'none' | 'internal' | 'external' | 'research';
    storageRetention: '30days' | '90days' | '1year' | '7years';
}

interface GeneratedNote {
    sections: {
        chiefComplaint: string;
        historyOfPresentIllness: string;
        mentalStatusExam: string;
        assessment: string;
        riskAssessment: {
            suicideRisk: 'Low' | 'Moderate' | 'High';
            violenceRisk: 'Low' | 'Moderate' | 'High';
            overallRisk: 'Low' | 'Moderate' | 'High';
        };
        plan: string;
        followUp: string[];
    };
    suggestedActions: string[];
    confidence: number;
    generatedAt: string;
    processingTime: number;
}

interface Alert {
    id: string;
    type: 'crisis' | 'violence' | 'medication' | 'risk' | 'safety';
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    message: string;
    keyword?: string;
    timestamp: string;
    acknowledged: boolean;
    actionRequired: boolean;
}

interface BillingData {
    consultationId: string;
    patientId: string;
    providerId: string;
    serviceDate: string;
    primaryCPT: {
        code: string;
        description: string;
        baseRate: number;
    };
    additionalCPTs: Array<{
        code: string;
        description: string;
        baseRate: number;
        type?: 'primary' | 'additional_service' | 'modifier';
    }>;
    diagnosisCodes: Array<{
        code: string;
        description: string;
        primary: boolean;
    }>;
    chargeAmount: number;
    allowedAmount: number;
    patientResponsibility: number;
    insuranceAmount: number;
    duration: number;
    modifiers: string[];
    status: string;
    metadata: {
        consultationType: string;
        priority: string;
        interpreterUsed: boolean;
        language: string;
        insuranceType: string;
        confidence: number;
    };
}

interface BillingRecommendation {
    type: 'warning' | 'review' | 'approval' | 'info';
    message: string;
}

export const DoctorDashboard: React.FC = () => {
    const [metrics, setMetrics] = useState<DoctorMetrics | null>(null);
    const [consultations, setConsultations] = useState<Consultation[]>([]);
    const [notifications, setNotifications] = useState<TeamsNotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<string>('schedule');
    const [currentUser, setCurrentUser] = useState<TeamsUser | null>(null);
    const [showVerification, setShowVerification] = useState(false);
    const [showConsent, setShowConsent] = useState(false);
    const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
    const [selectedPatientInfo, setSelectedPatientInfo] = useState<PatientInfo | null>(null);
    const [callInProgress, setCallInProgress] = useState(false);
    const [showNoteReview, setShowNoteReview] = useState(false);
    const [generatedNote, setGeneratedNote] = useState<GeneratedNote | null>(null);
    const [transcription, setTranscription] = useState<string>('');
    const [realTimeAlerts, setRealTimeAlerts] = useState<Alert[]>([]);
    const [alertsVisible, setAlertsVisible] = useState(true);
    const [showBillingReview, setShowBillingReview] = useState(false);
    const [billingData, setBillingData] = useState<BillingData | null>(null);
    const [billingRecommendations, setBillingRecommendations] = useState<BillingRecommendation[]>(
        []
    );

    useEffect(() => {
        loadDoctorData();
        setupNotificationHandlers();
        startNotificationTimer();

        return () => {
            socketService.off('doctorNotification');
            socketService.off('consultationUpdated');
            socketService.off('consultationReminder');
            socketService.off('transcriptionUpdate');
            socketService.off('realTimeAlert');
        };
    }, []);

    const loadDoctorData = async () => {
        try {
            setLoading(true);

            // Get current user context
            const userInfo = await teamsService.getCurrentUser();
            if (!userInfo) {
                console.error('Unable to get current user context');
                return;
            }
            setCurrentUser(userInfo);

            const today = new Date().toISOString().split('T')[0];

            // Load doctor's schedule and metrics
            const [scheduleData, metricsData] = await Promise.all([
                apiService.getDoctorSchedule(userInfo.id, today),
                apiService.getDashboardMetrics(),
            ]);

            // Process consultations with device information
            const processedConsultations = await Promise.all(
                scheduleData.map(async (consult: any) => {
                    const deviceInfo = await getDeviceInfo(consult.deviceId);
                    const minutesUntil = calculateMinutesUntil(consult.scheduledTime);

                    return {
                        ...consult,
                        deviceLocation: deviceInfo.location,
                        teamsId: deviceInfo.teamsId,
                        minutesUntil,
                        estimatedDuration: 45, // Default consultation duration
                    };
                })
            );

            setConsultations(processedConsultations);

            // Calculate metrics
            const nextAppointment = processedConsultations
                .filter((c) => c.status === 'scheduled' && c.minutesUntil > 0)
                .sort((a, b) => a.minutesUntil - b.minutesUntil)[0];

            setMetrics({
                todaysConsultations: {
                    completed: processedConsultations.filter((c) => c.status === 'completed')
                        .length,
                    total: processedConsultations.length,
                },
                nextAppointment: nextAppointment
                    ? {
                          time: formatTime(nextAppointment.scheduledTime),
                          patientName: nextAppointment.patientName,
                          minutesUntil: nextAppointment.minutesUntil,
                      }
                    : null,
                emergencyCalls: processedConsultations.filter((c) => c.priority === 'urgent')
                    .length,
                pendingNotes: processedConsultations.filter(
                    (c) => c.status === 'completed' && !c.notes
                ).length,
            });
        } catch (error) {
            console.error('Error loading doctor data:', error);
            // Fallback data for demo
            loadFallbackData();
        } finally {
            setLoading(false);
        }
    };

    const loadFallbackData = () => {
        const fallbackConsultations: Consultation[] = [
            {
                id: '1',
                scheduledTime: getTimeFromNow(15),
                patientName: 'Maria Gonzalez',
                patientId: 'P001',
                mrn: 'MRN-789123',
                consultationType: 'Crisis Intervention',
                priority: 'urgent',
                status: 'scheduled',
                deviceId: 'teams-ipad-emergency-room01',
                deviceLocation: 'Emergency Department, Room 01',
                teamsId: '19:emergency-room-01-device@thread.v2',
                interpreterRequired: true,
                language: 'Spanish',
                notes: 'Patient experiencing acute anxiety. Requires immediate attention.',
                minutesUntil: 15,
                estimatedDuration: 30,
            },
            {
                id: '2',
                scheduledTime: getTimeFromNow(45),
                patientName: 'James Wilson',
                patientId: 'P002',
                mrn: 'MRN-456789',
                consultationType: 'Follow-up Assessment',
                priority: 'standard',
                status: 'scheduled',
                deviceId: 'teams-ipad-floor3-room12',
                deviceLocation: 'Child Psychiatry Wing, Floor 3, Room 12',
                teamsId: '19:floor3-room12-device@thread.v2',
                interpreterRequired: false,
                language: 'English',
                notes: 'Follow-up for ADHD medication adjustment. Parent will be present.',
                minutesUntil: 45,
                estimatedDuration: 45,
            },
            {
                id: '3',
                scheduledTime: getTimeFromNow(90),
                patientName: 'Sophie Dubois',
                patientId: 'P003',
                mrn: 'MRN-321654',
                consultationType: 'Initial Consultation',
                priority: 'routine',
                status: 'scheduled',
                deviceId: 'teams-ipad-floor2-room08',
                deviceLocation: 'Psychiatry Wing, Floor 2, Room 08',
                teamsId: '19:floor2-room08-device@thread.v2',
                interpreterRequired: true,
                language: 'French',
                notes: 'New patient referral from primary care. Depression screening required.',
                minutesUntil: 90,
                estimatedDuration: 60,
            },
        ];

        setConsultations(fallbackConsultations);
        setMetrics({
            todaysConsultations: { completed: 3, total: 6 },
            nextAppointment: {
                time: formatTime(fallbackConsultations[0].scheduledTime),
                patientName: fallbackConsultations[0].patientName,
                minutesUntil: 15,
            },
            emergencyCalls: 1,
            pendingNotes: 2,
        });
    };

    const setupNotificationHandlers = () => {
        socketService.connect();

        // Handle new consultation assignments
        socketService.on('doctorNotification', (notification: any) => {
            console.log('📢 Doctor notification received:', notification);

            if (notification.doctorId === currentUser?.id) {
                addNotification({
                    id: Date.now().toString(),
                    consultationId: notification.consultId,
                    message: notification.message,
                    type: 'assignment',
                    timestamp: new Date().toISOString(),
                    read: false,
                });

                // Show Teams notification
                teamsService.sendNotification(currentUser?.id || '', {
                    title: 'New Consultation Assigned',
                    message: notification.message,
                });

                // Refresh consultations
                loadDoctorData();
            }
        });

        // Handle consultation status updates
        socketService.on('consultationUpdated', (update: any) => {
            setConsultations((prev) =>
                prev.map((consult) =>
                    consult.id === update.consultId
                        ? { ...consult, status: update.status }
                        : consult
                )
            );
        });

        // Handle reminder notifications
        socketService.on('consultationReminder', (reminder: any) => {
            addNotification({
                id: Date.now().toString(),
                consultationId: reminder.consultationId,
                message: `Reminder: Consultation with ${reminder.patientName} in 5 minutes`,
                type: 'reminder',
                timestamp: new Date().toISOString(),
                read: false,
            });

            // Show Teams notification
            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Consultation Reminder',
                message: `Your consultation with ${reminder.patientName} starts in 5 minutes`,
            });
        });

        // Handle real-time transcription updates
        socketService.on('transcriptionUpdate', (update: any) => {
            if (update.consultationId === selectedConsultation?.id) {
                setTranscription((prev) => prev + ' ' + update.text);

                // Process transcription chunk for real-time alerts
                processTranscriptionChunk(update.consultationId, update.text);
            }
        });

        // Handle real-time alerts
        socketService.on('realTimeAlert', (alert: Alert) => {
            setRealTimeAlerts((prev) => [alert, ...prev]);

            // Show critical alert notification
            if (alert.severity === 'critical' || alert.severity === 'high') {
                teamsService.sendNotification(currentUser?.id || '', {
                    title: alert.title,
                    message: alert.message,
                });
            }
        });
    };

    const startNotificationTimer = () => {
        // Check for 5-minute reminders every minute
        const timer = setInterval(() => {
            consultations.forEach((consult) => {
                if (consult.status === 'scheduled' && consult.minutesUntil === 5) {
                    // Send 5-minute reminder
                    socketService.emit('sendReminder', {
                        consultationId: consult.id,
                        patientName: consult.patientName,
                        doctorId: currentUser?.id,
                    });
                }
            });
        }, 60000); // Check every minute

        return () => clearInterval(timer);
    };

    const addNotification = (notification: TeamsNotification) => {
        setNotifications((prev) => [notification, ...prev.slice(0, 9)]); // Keep last 10
    };

    const handleStartCall = async (consultation: Consultation) => {
        try {
            console.log(`🎥 Initiating call flow for consultation ${consultation.id}`);

            // Step 1: Load patient information from Epic
            const patientInfo = await loadPatientInfo(consultation.patientId);
            setSelectedPatientInfo(patientInfo);
            setSelectedConsultation(consultation);

            // Step 2: Show patient verification dialog
            setShowVerification(true);
        } catch (error) {
            console.error('Error initiating call flow:', error);
            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Call Initiation Failed',
                message: 'Unable to load patient information. Please try again.',
            });
        }
    };

    const handleVerificationConfirm = () => {
        setShowVerification(false);
        setShowConsent(true);
    };

    const handleVerificationCancel = () => {
        setShowVerification(false);
        setSelectedConsultation(null);
        setSelectedPatientInfo(null);
    };

    const handleConsentComplete = async (consentData: ConsentOptions) => {
        if (!selectedConsultation) return;

        try {
            setCallInProgress(true);
            console.log(`🎥 Starting Teams consultation: ${selectedConsultation.id}`);

            // Store consent data first
            await apiService.storeConsentData(selectedConsultation.id, consentData);

            // Start Teams consultation using the new integration service
            const callResult = await teamsService.startCall({
                consultationId: selectedConsultation.id,
                patientName: selectedConsultation.patientName,
                interpreterRequired: selectedConsultation.interpreterRequired,
                language: selectedConsultation.language || 'English',
            });

            if (callResult.success) {
                // Update consultation status in local state
                setConsultations((prev) =>
                    prev.map((c) =>
                        c.id === selectedConsultation.id ? { ...c, status: 'in_progress' } : c
                    )
                );

                // Close consent dialog
                setShowConsent(false);
                setSelectedConsultation(null);
                setSelectedPatientInfo(null);

                // Show success notification
                await teamsService.sendNotification(currentUser?.id || '', {
                    title: 'Teams Meeting Started',
                    message: `Consultation with ${selectedConsultation.patientName} started successfully`,
                });

                console.log('✅ Teams consultation started successfully');
            } else {
                throw new Error(callResult.error || 'Failed to start Teams consultation');
            }
        } catch (error) {
            console.error('❌ Error starting Teams consultation:', error);

            // Show error notification
            await teamsService.sendNotification(currentUser?.id || '', {
                title: 'Consultation Start Failed',
                message:
                    error instanceof Error
                        ? error.message
                        : 'Unable to start the consultation. Please try again.',
            });
        } finally {
            setCallInProgress(false);
        }
    };

    const handleConsentCancel = () => {
        setShowConsent(false);
        setSelectedConsultation(null);
        setSelectedPatientInfo(null);
    };

    const loadPatientInfo = async (patientId: string): Promise<PatientInfo> => {
        try {
            // In a real implementation, this would fetch from Epic FHIR API
            const response = await apiService.getPatientData(patientId);
            return response;
        } catch (error) {
            // Fallback with demo data for the current implementation
            const fallbackPatients: { [key: string]: PatientInfo } = {
                P001: {
                    id: 'P001',
                    name: 'Maria Gonzalez',
                    mrn: 'MRN-789123',
                    dateOfBirth: '1987-03-15',
                    age: 37,
                    gender: 'Female',
                    language: 'Spanish',
                    interpreterRequired: true,
                    medicalConditions: ['Anxiety Disorder', 'Depression'],
                    allergies: ['Penicillin'],
                    riskLevel: 'high',
                    consultationType: 'Crisis Intervention',
                    priority: 'urgent',
                },
                P002: {
                    id: 'P002',
                    name: 'James Wilson',
                    mrn: 'MRN-456789',
                    dateOfBirth: '2010-08-22',
                    age: 14,
                    gender: 'Male',
                    language: 'English',
                    interpreterRequired: false,
                    medicalConditions: ['ADHD'],
                    allergies: [],
                    riskLevel: 'medium',
                    consultationType: 'Follow-up Assessment',
                    priority: 'standard',
                },
                P003: {
                    id: 'P003',
                    name: 'Sophie Dubois',
                    mrn: 'MRN-321654',
                    dateOfBirth: '1992-11-30',
                    age: 32,
                    gender: 'Female',
                    language: 'French',
                    interpreterRequired: true,
                    medicalConditions: ['Depression', 'Sleep Disorder'],
                    allergies: ['Latex', 'Shellfish'],
                    riskLevel: 'medium',
                    consultationType: 'Initial Consultation',
                    priority: 'routine',
                },
            };

            return (
                fallbackPatients[patientId] || fallbackPatients.P001 // Default fallback
            );
        }
    };

    const processTranscriptionChunk = async (consultationId: string, chunk: string) => {
        try {
            await apiService.processTranscriptionChunk(consultationId, chunk);
        } catch (error) {
            console.error('Error processing transcription chunk:', error);
        }
    };

    const handleGenerateNote = async (consultation: Consultation) => {
        try {
            // Get the current transcription (in real app, this would be from the ongoing call)
            const mockTranscription = `
                Doctor: Good morning, how are you feeling today?
                Patient: I've been feeling really down lately, especially in the mornings.
                Doctor: Can you tell me more about these feelings?
                Patient: It's like I can't get out of bed, everything feels overwhelming.
                Doctor: How long have you been experiencing these symptoms?
                Patient: About 3 weeks now, ever since I lost my job.
                Doctor: I understand. Have you had any thoughts of harming yourself?
                Patient: Sometimes I think about it, but I wouldn't actually do anything.
                Doctor: Thank you for being honest with me. Let's talk about getting you some help.
            `;

            const consultationContext = {
                consultationType: consultation.consultationType,
                patientAge: selectedPatientInfo?.age || 30,
                patientGender: selectedPatientInfo?.gender || 'Unknown',
                priority: consultation.priority,
                interpreterRequired: consultation.interpreterRequired,
                language: consultation.language,
                medicalHistory: selectedPatientInfo?.medicalConditions || [],
                allergies: selectedPatientInfo?.allergies || [],
                currentMedications: [],
            };

            const result = await apiService.generateClinicalNote(
                consultation.id,
                mockTranscription,
                consultationContext
            );

            if (result.success) {
                // Create the note structure for the interface
                const note: GeneratedNote = {
                    sections: {
                        chiefComplaint:
                            "Patient reports feeling 'really down lately, especially in the mornings' for approximately 3 weeks.",
                        historyOfPresentIllness:
                            'Patient describes persistent depressed mood with morning worsening for 3 weeks, coinciding with job loss. Reports difficulty getting out of bed and feeling overwhelmed. Endorses passive suicidal ideation without intent or plan.',
                        mentalStatusExam:
                            'Patient appears casually dressed, makes good eye contact. Speech is soft and slow. Mood is depressed, affect is congruent and restricted. Thought process is linear. Denies current suicidal intent but endorses passive ideation. No psychotic symptoms noted.',
                        assessment:
                            'Patient presents with symptoms consistent with Major Depressive Episode, likely precipitated by recent job loss. Risk assessment indicates moderate risk given passive suicidal ideation.',
                        riskAssessment: result.riskAssessment || {
                            suicideRisk: 'Moderate',
                            violenceRisk: 'Low',
                            overallRisk: 'Moderate',
                        },
                        plan: '1. Initiate antidepressant therapy\n2. Schedule follow-up in 2 weeks\n3. Safety planning discussed\n4. Referral to therapist for CBT\n5. Patient education about depression provided',
                        followUp: [
                            'Follow-up appointment in 2 weeks',
                            'Therapy referral within 1 week',
                            'Safety check-in in 3 days',
                        ],
                    },
                    suggestedActions: result.suggestedActions || [
                        'Consider starting SSRI antidepressant',
                        'Schedule therapy consultation',
                        'Provide crisis hotline numbers',
                        'Monitor for suicidal ideation',
                    ],
                    confidence: result.confidence || 87,
                    generatedAt: new Date().toISOString(),
                    processingTime: result.processingTime || 2.3,
                };

                setGeneratedNote(note);
                setTranscription(mockTranscription);
                setSelectedConsultation(consultation);
                setShowNoteReview(true);
            }
        } catch (error) {
            console.error('Error generating note:', error);
            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Note Generation Failed',
                message: 'Unable to generate clinical note. Please try again.',
            });
        }
    };

    const handleSaveNote = async (editedNote: GeneratedNote) => {
        if (!selectedConsultation) return;

        try {
            await apiService.saveDraftNote(selectedConsultation.id, editedNote, {
                savedAt: new Date(),
                editCount: 1,
            });
        } catch (error) {
            console.error('Error saving note:', error);
        }
    };

    const handleSubmitNote = async (finalNote: GeneratedNote) => {
        if (!selectedConsultation) return;

        try {
            await apiService.submitFinalNote(selectedConsultation.id, finalNote, {
                submittedAt: new Date(),
                finalEditCount: 1,
            });

            setShowNoteReview(false);
            setGeneratedNote(null);
            setSelectedConsultation(null);

            // Refresh consultations to show updated status
            loadDoctorData();
        } catch (error) {
            console.error('Error submitting note:', error);
        }
    };

    const handleNoteCancel = () => {
        setShowNoteReview(false);
        setGeneratedNote(null);
        setSelectedConsultation(null);
        setTranscription('');
    };

    const handleAcknowledgeAlert = (alertId: string) => {
        setRealTimeAlerts((prev) =>
            prev.map((alert) => (alert.id === alertId ? { ...alert, acknowledged: true } : alert))
        );
    };

    const handleTakeAction = async (alertId: string, action: string) => {
        console.log(`Taking action ${action} for alert ${alertId}`);

        // Handle specific actions
        switch (action) {
            case 'crisis_protocol':
                teamsService.sendNotification(currentUser?.id || '', {
                    title: 'Crisis Protocol Activated',
                    message: 'Emergency response team notified',
                });
                break;
            case 'contact_security':
                teamsService.sendNotification(currentUser?.id || '', {
                    title: 'Security Contacted',
                    message: 'Security team is responding',
                });
                break;
            default:
                break;
        }

        // Acknowledge the alert after action
        handleAcknowledgeAlert(alertId);
    };

    const handleGenerateBilling = async (consultation: Consultation) => {
        try {
            console.log(`🧾 Generating billing for consultation ${consultation.id}`);

            const result = await apiService.generateBilling(
                consultation.id,
                'commercial', // Default insurance type
                {
                    copay: 25.0,
                    coinsurance: 0.2,
                    remainingDeductible: 100.0,
                }
            );

            if (result.success) {
                setBillingData(result.billingData);
                setBillingRecommendations(result.recommendations || []);
                setSelectedConsultation(consultation);
                setShowBillingReview(true);
            }
        } catch (error) {
            console.error('Error generating billing:', error);
            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Billing Generation Failed',
                message: 'Unable to generate billing data. Please try again.',
            });
        }
    };

    const handleApproveBilling = async (approvedBillingData: BillingData) => {
        if (!selectedConsultation) return;

        try {
            await apiService.approveBilling(
                selectedConsultation.id,
                approvedBillingData,
                'Approved via doctor dashboard'
            );

            setShowBillingReview(false);
            setBillingData(null);
            setSelectedConsultation(null);

            // Refresh consultations to show updated billing status
            loadDoctorData();

            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Billing Approved',
                message: 'Billing has been approved and submitted to Epic EHR',
            });
        } catch (error) {
            console.error('Error approving billing:', error);
            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Billing Approval Failed',
                message: 'Unable to approve billing. Please try again.',
            });
        }
    };

    const handleRejectBilling = async (reason: string) => {
        if (!selectedConsultation) return;

        try {
            await apiService.rejectBilling(selectedConsultation.id, reason);

            setShowBillingReview(false);
            setBillingData(null);
            setSelectedConsultation(null);

            // Refresh consultations
            loadDoctorData();

            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Billing Rejected',
                message: 'Billing has been rejected and will require manual review',
            });
        } catch (error) {
            console.error('Error rejecting billing:', error);
        }
    };

    const handleEditBilling = async (editedBillingData: BillingData) => {
        if (!selectedConsultation) return;

        try {
            await apiService.reviewBilling(
                selectedConsultation.id,
                editedBillingData,
                'Edited via doctor dashboard'
            );

            setBillingData(editedBillingData);

            teamsService.sendNotification(currentUser?.id || '', {
                title: 'Billing Updated',
                message: 'Billing changes have been saved',
            });
        } catch (error) {
            console.error('Error updating billing:', error);
        }
    };

    const handleBillingCancel = () => {
        setShowBillingReview(false);
        setBillingData(null);
        setSelectedConsultation(null);
        setBillingRecommendations([]);
    };

    const getPriorityBadge = (priority: string) => {
        switch (priority) {
            case 'urgent':
                return (
                    <Badge appearance="filled" color="danger">
                        Urgent
                    </Badge>
                );
            case 'standard':
                return (
                    <Badge appearance="tint" color="warning">
                        Standard
                    </Badge>
                );
            case 'routine':
                return (
                    <Badge appearance="outline" color="success">
                        Routine
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'scheduled':
                return (
                    <Badge appearance="tint" color="brand">
                        Scheduled
                    </Badge>
                );
            case 'in_progress':
                return (
                    <Badge appearance="filled" color="success">
                        In Progress
                    </Badge>
                );
            case 'completed':
                return (
                    <Badge appearance="ghost" color="subtle">
                        Completed
                    </Badge>
                );
            case 'emergency':
                return (
                    <Badge appearance="filled" color="danger">
                        Emergency
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const formatTime = (dateTime: string) => {
        return new Date(dateTime).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };

    const calculateMinutesUntil = (scheduledTime: string) => {
        const now = new Date();
        const scheduled = new Date(scheduledTime);
        return Math.max(0, Math.floor((scheduled.getTime() - now.getTime()) / 60000));
    };

    const getTimeFromNow = (minutes: number) => {
        const time = new Date();
        time.setMinutes(time.getMinutes() + minutes);
        return time.toISOString();
    };

    const getDeviceInfo = async (deviceId: string) => {
        // This would normally call the device service API
        const deviceMap: { [key: string]: any } = {
            'teams-ipad-emergency-room01': {
                location: 'Emergency Department, Room 01',
                teamsId: '19:emergency-room-01-device@thread.v2',
            },
            'teams-ipad-floor3-room12': {
                location: 'Child Psychiatry Wing, Floor 3, Room 12',
                teamsId: '19:floor3-room12-device@thread.v2',
            },
            'teams-ipad-floor2-room08': {
                location: 'Psychiatry Wing, Floor 2, Room 08',
                teamsId: '19:floor2-room08-device@thread.v2',
            },
        };
        return deviceMap[deviceId] || { location: 'Unknown', teamsId: 'unknown' };
    };

    const createNewConsultation = async (consultationData: {
        patientName: string;
        consultationType: string;
        scheduledTime: string;
        priority?: string;
        interpreterRequired?: boolean;
        language?: string;
    }) => {
        try {
            if (!currentUser) {
                throw new Error('User context not available');
            }

            console.log('🚀 Creating new Teams consultation');

            // Create consultation with Teams meeting
            const result = await teamsService.createConsultation({
                consultationId: `consult-${Date.now()}`, // Temporary ID, backend will assign real ID
                patientInfo: {
                    name: consultationData.patientName,
                    language: consultationData.language || 'English',
                },
                doctorId: currentUser.id,
                scheduledTime: consultationData.scheduledTime,
                estimatedDuration: 60,
                interpreterRequired: consultationData.interpreterRequired || false,
                priority: consultationData.priority || 'routine',
            });

            if (result.success && result.consultation) {
                // Add to local state
                const newConsultation: Consultation = {
                    id: result.consultation.id,
                    patientId: result.consultation.patientName,
                    patientName: result.consultation.patientName,
                    mrn: `MRN-${Date.now()}`, // Temporary MRN
                    consultationType: consultationData.consultationType,
                    scheduledTime: consultationData.scheduledTime,
                    status: 'scheduled' as const,
                    priority: (consultationData.priority || 'routine') as
                        | 'urgent'
                        | 'standard'
                        | 'routine',
                    interpreterRequired: consultationData.interpreterRequired || false,
                    language: consultationData.language || 'English',
                    deviceId: 'teams-virtual', // Virtual device for Teams meetings
                    deviceLocation: 'Teams Meeting',
                    teamsId: result.consultation.teamsMeetingId,
                    notes: '',
                    minutesUntil: calculateMinutesUntil(consultationData.scheduledTime),
                    estimatedDuration: result.consultation.estimatedDuration || 60,
                };

                setConsultations((prev) => [...prev, newConsultation]);

                // Show success notification
                await teamsService.sendNotification(currentUser.id, {
                    title: 'Consultation Scheduled',
                    message: `Teams meeting created for ${consultationData.patientName}`,
                    actionUrl: result.consultation.teamsJoinUrl,
                });

                console.log('✅ Teams consultation created successfully');
                return result.consultation;
            } else {
                throw new Error(result.error || 'Failed to create consultation');
            }
        } catch (error) {
            console.error('❌ Error creating consultation:', error);

            await teamsService.sendNotification(currentUser?.id || '', {
                title: 'Consultation Creation Failed',
                message: error instanceof Error ? error.message : 'Unable to create consultation',
            });

            throw error;
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                }}
            >
                <Spinner size="large" label="Loading your consultations..." />
            </div>
        );
    }

    const renderScheduleTab = () => (
        <div>
            {/* Next Appointment Alert */}
            {metrics?.nextAppointment && (
                <Card
                    style={{
                        padding: '20px',
                        marginBottom: '24px',
                        backgroundColor:
                            metrics.nextAppointment.minutesUntil <= 10 ? '#fff4e6' : '#f3f2f1',
                        border:
                            metrics.nextAppointment.minutesUntil <= 10
                                ? '2px solid #ffa500'
                                : '1px solid #e1e5e9',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Clock24Regular
                            style={{
                                fontSize: '32px',
                                color:
                                    metrics.nextAppointment.minutesUntil <= 10
                                        ? '#ffa500'
                                        : '#0078d4',
                            }}
                        />
                        <div style={{ flex: 1 }}>
                            <Text size={600} weight="semibold">
                                Next: {metrics.nextAppointment.patientName} at{' '}
                                {metrics.nextAppointment.time}
                            </Text>
                            <Text size={400} style={{ display: 'block', color: '#605e5c' }}>
                                {metrics.nextAppointment.minutesUntil <= 10
                                    ? `⚠️ Starting in ${metrics.nextAppointment.minutesUntil} minutes!`
                                    : `Starting in ${metrics.nextAppointment.minutesUntil} minutes`}
                            </Text>
                        </div>
                    </div>
                </Card>
            )}

            {/* Consultations List */}
            <Card style={{ padding: '24px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '20px' }}>
                    Today's Consultations ({consultations.length})
                </Text>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {consultations.map((consultation) => (
                        <Card
                            key={consultation.id}
                            style={{
                                padding: '20px',
                                border:
                                    consultation.priority === 'urgent'
                                        ? '2px solid #d13438'
                                        : '1px solid #e1e5e9',
                                backgroundColor:
                                    consultation.status === 'in_progress' ? '#e8f5e8' : '#ffffff',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: '16px',
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        <Text size={500} weight="semibold">
                                            {formatTime(consultation.scheduledTime)} -{' '}
                                            {consultation.patientName}
                                        </Text>
                                        {getPriorityBadge(consultation.priority)}
                                        {getStatusBadge(consultation.status)}
                                    </div>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '12px',
                                            marginBottom: '12px',
                                        }}
                                    >
                                        <div>
                                            <Text
                                                size={300}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                <strong>MRN:</strong> {consultation.mrn}
                                            </Text>
                                            <Text
                                                size={300}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                <strong>Type:</strong>{' '}
                                                {consultation.consultationType}
                                            </Text>
                                            <Text
                                                size={300}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                <strong>Duration:</strong>{' '}
                                                {consultation.estimatedDuration} minutes
                                            </Text>
                                        </div>
                                        <div>
                                            <Text
                                                size={300}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                <strong>Device ID:</strong> {consultation.deviceId}
                                            </Text>
                                            <Text
                                                size={300}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                <strong>Location:</strong>{' '}
                                                {consultation.deviceLocation}
                                            </Text>
                                            <Text
                                                size={300}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                <strong>Language:</strong> {consultation.language}
                                                {consultation.interpreterRequired && (
                                                    <Badge
                                                        size="small"
                                                        color="warning"
                                                        style={{ marginLeft: '8px' }}
                                                    >
                                                        Interpreter
                                                    </Badge>
                                                )}
                                            </Text>
                                        </div>
                                    </div>

                                    {consultation.notes && (
                                        <div
                                            style={{
                                                marginBottom: '12px',
                                                padding: '12px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <Text size={300}>
                                                <strong>Notes:</strong> {consultation.notes}
                                            </Text>
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginLeft: '20px' }}>
                                    {consultation.status === 'scheduled' && (
                                        <>
                                            <Button
                                                appearance="primary"
                                                size="large"
                                                icon={<Call24Regular />}
                                                onClick={() => handleStartCall(consultation)}
                                                style={{
                                                    backgroundColor:
                                                        consultation.priority === 'urgent'
                                                            ? '#d13438'
                                                            : undefined,
                                                }}
                                            >
                                                Start Call
                                            </Button>
                                            {consultation.deviceId && (
                                                <Button
                                                    appearance="secondary"
                                                    size="large"
                                                    icon={<DeviceMeetingRoom24Regular />}
                                                    style={{ marginLeft: '8px' }}
                                                    onClick={async () => {
                                                        try {
                                                            setCallInProgress(true);
                                                            const result =
                                                                await teamsService.startDeviceCall({
                                                                    consultationId: consultation.id,
                                                                    deviceId: consultation.deviceId,
                                                                });
                                                            if (result.success) {
                                                                setConsultations((prev) =>
                                                                    prev.map((c) =>
                                                                        c.id === consultation.id
                                                                            ? {
                                                                                  ...c,
                                                                                  status: 'in_progress',
                                                                              }
                                                                            : c
                                                                    )
                                                                );
                                                                await teamsService.sendNotification(
                                                                    currentUser?.id || '',
                                                                    {
                                                                        title: 'Device Call Started',
                                                                        message: `Device-based Teams call started for ${consultation.patientName}`,
                                                                    }
                                                                );
                                                            } else {
                                                                throw new Error(
                                                                    result.error ||
                                                                        'Failed to start device call'
                                                                );
                                                            }
                                                        } catch (error) {
                                                            await teamsService.sendNotification(
                                                                currentUser?.id || '',
                                                                {
                                                                    title: 'Device Call Failed',
                                                                    message:
                                                                        error instanceof Error
                                                                            ? error.message
                                                                            : 'Unable to start device call. Please try again.',
                                                                }
                                                            );
                                                        } finally {
                                                            setCallInProgress(false);
                                                        }
                                                    }}
                                                    disabled={callInProgress}
                                                >
                                                    Start Device Call
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    {consultation.status === 'in_progress' && (
                                        <Button
                                            appearance="subtle"
                                            size="large"
                                            icon={<VideoClip24Regular />}
                                            disabled
                                        >
                                            In Progress
                                        </Button>
                                    )}
                                    {consultation.status === 'completed' && (
                                        <>
                                            <Button
                                                appearance="primary"
                                                size="medium"
                                                icon={<Document24Regular />}
                                                onClick={() => handleGenerateNote(consultation)}
                                                style={{ marginRight: '8px' }}
                                            >
                                                Generate Note
                                            </Button>
                                            <Button
                                                appearance="primary"
                                                size="medium"
                                                icon={<Money24Regular />}
                                                onClick={() => handleGenerateBilling(consultation)}
                                                style={{ marginRight: '8px' }}
                                            >
                                                Generate Billing
                                            </Button>
                                            <Button
                                                appearance="outline"
                                                size="medium"
                                                icon={<Note24Regular />}
                                            >
                                                Review Notes
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {consultations.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#605e5c' }}>
                        <Calendar24Regular style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <Text size={500}>No consultations scheduled for today</Text>
                    </div>
                )}
            </Card>
        </div>
    );

    const renderNotificationsTab = () => (
        <Card style={{ padding: '24px' }}>
            <Text size={600} weight="semibold" style={{ marginBottom: '20px' }}>
                Recent Notifications ({notifications.length})
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {notifications.map((notification) => (
                    <Card
                        key={notification.id}
                        style={{
                            padding: '16px',
                            backgroundColor: notification.read ? '#f8f9fa' : '#ffffff',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {notification.type === 'reminder' && (
                                <Clock24Regular style={{ color: '#ffa500' }} />
                            )}
                            {notification.type === 'assignment' && (
                                <Person24Regular style={{ color: '#0078d4' }} />
                            )}
                            {notification.type === 'emergency' && (
                                <Alert24Regular style={{ color: '#d13438' }} />
                            )}

                            <div style={{ flex: 1 }}>
                                <Text
                                    size={400}
                                    weight={notification.read ? 'regular' : 'semibold'}
                                >
                                    {notification.message}
                                </Text>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    {new Date(notification.timestamp).toLocaleString()}
                                </Text>
                            </div>
                        </div>
                    </Card>
                ))}

                {notifications.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#605e5c' }}>
                        <Text size={400}>No notifications</Text>
                    </div>
                )}
            </div>
        </Card>
    );

    return (
        <div style={{ padding: '0' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <Text size={900} weight="bold">
                    Doctor Dashboard
                </Text>
                <Text size={300} style={{ display: 'block', color: '#605e5c', marginTop: '4px' }}>
                    Welcome back, {currentUser?.displayName || 'Doctor'}
                </Text>
            </div>

            {/* Metrics Cards */}
            {metrics && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '20px',
                        marginBottom: '24px',
                    }}
                >
                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Calendar24Regular style={{ fontSize: '32px', color: '#0078d4' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Today's Progress
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.todaysConsultations.completed}/
                                    {metrics.todaysConsultations.total}
                                </Text>
                                <ProgressBar
                                    value={
                                        metrics.todaysConsultations.completed /
                                        metrics.todaysConsultations.total
                                    }
                                    style={{ marginTop: '8px' }}
                                />
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Warning24Regular style={{ fontSize: '32px', color: '#d13438' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Urgent Cases
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.emergencyCalls}
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Document24Regular style={{ fontSize: '32px', color: '#107c10' }} />
                            <div>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Pending Notes
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.pendingNotes}
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Navigation Tabs */}
            <TabList
                selectedValue={activeTab}
                onTabSelect={(event, data) => setActiveTab(data.value as string)}
                style={{ marginBottom: '24px' }}
            >
                <Tab value="schedule" icon={<Calendar24Regular />}>
                    Schedule ({consultations.filter((c) => c.status === 'scheduled').length})
                </Tab>
                <Tab value="notifications" icon={<Alert24Regular />}>
                    Notifications ({notifications.filter((n) => !n.read).length})
                </Tab>
            </TabList>

            {/* Tab Content */}
            {activeTab === 'schedule' && renderScheduleTab()}
            {activeTab === 'notifications' && renderNotificationsTab()}

            {/* Real-time Alerts */}
            <RealTimeAlerts
                consultationId={selectedConsultation?.id || ''}
                alerts={realTimeAlerts}
                onAcknowledgeAlert={handleAcknowledgeAlert}
                onTakeAction={handleTakeAction}
                isVisible={alertsVisible && realTimeAlerts.some((alert) => !alert.acknowledged)}
            />

            {/* Patient Verification Dialog */}
            {showVerification && selectedPatientInfo && selectedConsultation && (
                <PatientVerification
                    isOpen={showVerification}
                    patientInfo={selectedPatientInfo}
                    deviceLocation={selectedConsultation.deviceLocation}
                    onConfirm={handleVerificationConfirm}
                    onCancel={handleVerificationCancel}
                    loading={false}
                />
            )}

            {/* Consent Capture Dialog */}
            {showConsent && selectedPatientInfo && selectedConsultation && (
                <ConsentCapture
                    isOpen={showConsent}
                    patientName={selectedPatientInfo.name}
                    consultationType={selectedPatientInfo.consultationType}
                    language={selectedPatientInfo.language}
                    interpreterRequired={selectedPatientInfo.interpreterRequired}
                    onConsent={handleConsentComplete}
                    onCancel={handleConsentCancel}
                    loading={callInProgress}
                />
            )}

            {/* Note Review Interface */}
            {showNoteReview && generatedNote && selectedConsultation && (
                <NoteReviewInterface
                    isOpen={showNoteReview}
                    consultationId={selectedConsultation.id}
                    patientName={selectedConsultation.patientName}
                    consultationType={selectedConsultation.consultationType}
                    transcription={transcription}
                    generatedNote={generatedNote}
                    onSave={handleSaveNote}
                    onSubmit={handleSubmitNote}
                    onCancel={handleNoteCancel}
                    loading={false}
                />
            )}

            {/* Billing Review Interface */}
            {showBillingReview && billingData && selectedConsultation && (
                <BillingReviewInterface
                    isOpen={showBillingReview}
                    consultationId={selectedConsultation.id}
                    patientName={selectedConsultation.patientName}
                    billingData={billingData}
                    recommendations={billingRecommendations}
                    onApprove={handleApproveBilling}
                    onReject={handleRejectBilling}
                    onEdit={handleEditBilling}
                    onCancel={handleBillingCancel}
                    loading={false}
                />
            )}
        </div>
    );
};
